/**
 * Offline Game Plan Service
 * 
 * Handles game plan operations in offline mode using cached permissions
 * and IndexedDB storage, bypassing direct Supabase auth calls
 */

import { GamePlan } from '@/types/gamePlanner';
import { GamePlanOffline } from '@/services/offline/indexeddb/schema';
import { indexedDBService } from '@/services/offline/indexeddb';
import { supabase } from '@/integrations/supabase/client';
import { offlineActionQueue } from '@/contexts/offline/queue/OfflineActionQueue';
import { Node, Edge } from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';

/**
 * Transform GamePlan to GamePlanOffline
 */
export function transformToOfflineGamePlan(
  gamePlan: GamePlan,
  userId: string,
  isOfflineCreated = false
): GamePlanOffline {
  return {
    id: gamePlan.id,
    name: gamePlan.name,
    description: gamePlan.description,
    thumbnail: gamePlan.thumbnail,
    flow_data: {
      nodes: gamePlan.flow_data?.nodes || [],
      edges: gamePlan.flow_data?.edges || []
    },
    user_id: userId,
    is_public: (gamePlan.is_public || false) ? 1 : 0, // Convert to number for IndexedDB
    is_template: (gamePlan.is_template || false) ? 1 : 0, // Convert to number for IndexedDB
    is_admin_template: (gamePlan.is_admin_template || false) ? 1 : 0, // Convert to number for IndexedDB
    created_at: gamePlan.created_at || new Date().toISOString(),
    updated_at: gamePlan.updated_at || new Date().toISOString(),
    _offline_created: isOfflineCreated,
    _offline_updated: false,
    _sync_status: isOfflineCreated ? 'pending' : 'synced',
    _last_modified: new Date().toISOString()
  };
}

/**
 * Transform GamePlanOffline to GamePlan
 */
export function transformFromOfflineGamePlan(offlineGamePlan: GamePlanOffline): GamePlan {
  return {
    id: offlineGamePlan.id,
    name: offlineGamePlan.name,
    description: offlineGamePlan.description,
    thumbnail: offlineGamePlan.thumbnail,
    flow_data: {
      nodes: (offlineGamePlan.flow_data?.nodes || []) as Node[],
      edges: (offlineGamePlan.flow_data?.edges || []) as Edge[]
    },
    user_id: offlineGamePlan.user_id,
    is_public: Boolean(offlineGamePlan.is_public), // Convert from number to boolean
    is_template: Boolean(offlineGamePlan.is_template), // Convert from number to boolean
    is_admin_template: Boolean(offlineGamePlan.is_admin_template), // Convert from number to boolean
    created_at: offlineGamePlan.created_at,
    updated_at: offlineGamePlan.updated_at,
    last_updated: offlineGamePlan.updated_at
  };
}

/**
 * Offline Game Plan Service Class
 */
export class OfflineGamePlanService {
  private static instance: OfflineGamePlanService;

  static getInstance(): OfflineGamePlanService {
    if (!OfflineGamePlanService.instance) {
      OfflineGamePlanService.instance = new OfflineGamePlanService();
    }
    return OfflineGamePlanService.instance;
  }

  /**
   * Check if user has permission for game plan operations
   */
  private async checkPermissions(): Promise<{ isAuthenticated: boolean; userId: string | null }> {
    try {
      // Get current user from Supabase auth (more reliable than cache)
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user?.id) {
        return { isAuthenticated: false, userId: null };
      }

      return {
        isAuthenticated: true,
        userId: session.user.id
      };
    } catch (error) {
      return { isAuthenticated: false, userId: null };
    }
  }

  /**
   * Get user's game plans (non-templates)
   */
  async getUserGamePlans(): Promise<GamePlan[]> {
    const { isAuthenticated, userId } = await this.checkPermissions();
    if (!isAuthenticated || !userId) {
      throw new Error('Authentication required to access game plans');
    }

    await indexedDBService.initialize();
    const offlinePlans = await indexedDBService.gamePlans.getByUserId(userId);
    return offlinePlans.map(transformFromOfflineGamePlan);
  }

  /**
   * Get all templates (user, admin, public)
   */
  async getAllTemplates(): Promise<GamePlan[]> {
    const { isAuthenticated, userId } = await this.checkPermissions();
    if (!isAuthenticated || !userId) {
      throw new Error('Authentication required to access templates');
    }

    await indexedDBService.initialize();
    
    // Get user's own templates
    const userTemplates = await indexedDBService.gamePlans.getTemplatesByUserId(userId);
    
    // Get admin templates
    const adminTemplates = await indexedDBService.gamePlans.getAdminTemplates();
    
    // Get public templates (excluding user's own)
    const allPublicTemplates = await indexedDBService.gamePlans.getPublicTemplates();
    const publicTemplates = allPublicTemplates.filter(template => template.user_id !== userId);
    
    // Combine and deduplicate
    const allTemplates = [...userTemplates, ...adminTemplates, ...publicTemplates];
    const uniqueTemplates = allTemplates.filter((template, index, self) =>
      index === self.findIndex(t => t.id === template.id)
    );
    
    return uniqueTemplates.map(transformFromOfflineGamePlan);
  }

  /**
   * Create a new game plan in offline mode
   */
  async createGamePlan(params: {
    name: string;
    description?: string;
    nodes: Node[];
    edges: Edge[];
    thumbnail?: string;
    isTemplate?: boolean;
    isPublic?: boolean;
  }): Promise<GamePlan> {
    const { isAuthenticated, userId } = await this.checkPermissions();
    if (!isAuthenticated || !userId) {
      throw new Error('Authentication required to create game plans');
    }

    const gameId = uuidv4();
    const now = new Date().toISOString();

    const newGamePlan: GamePlanOffline = {
      id: gameId,
      name: params.name,
      description: params.description,
      thumbnail: params.thumbnail,
      flow_data: {
        nodes: params.nodes,
        edges: params.edges,
        thumbnail: params.thumbnail
      },
      user_id: userId,
      is_public: (params.isPublic || false) ? 1 : 0, // Convert to number for IndexedDB
      is_template: (params.isTemplate || false) ? 1 : 0, // Convert to number for IndexedDB
      is_admin_template: 0, // Convert to number for IndexedDB
      created_at: now,
      updated_at: now,
      _offline_created: true,
      _offline_updated: false,
      _sync_status: 'pending',
      _last_modified: now
    };

    await indexedDBService.initialize();
    await indexedDBService.gamePlans.create(newGamePlan);

    // Queue for sync when online
    await offlineActionQueue.enqueueAction(
      'create_game_plan',
      newGamePlan as unknown as Record<string, unknown>,
      userId
    );

    return transformFromOfflineGamePlan(newGamePlan);
  }

  /**
   * Update an existing game plan in offline mode
   */
  async updateGamePlan(gamePlan: GamePlan): Promise<GamePlan> {
    const { isAuthenticated, userId } = await this.checkPermissions();
    if (!isAuthenticated || !userId) {
      throw new Error('Authentication required to update game plans');
    }

    await indexedDBService.initialize();
    
    // Get existing plan
    const existingPlan = await indexedDBService.gamePlans.get(gamePlan.id);
    if (!existingPlan) {
      throw new Error('Game plan not found');
    }

    // Check ownership
    if (existingPlan.user_id !== userId) {
      throw new Error('You can only update your own game plans');
    }

    const updatedPlan: GamePlanOffline = {
      ...existingPlan,
      name: gamePlan.name,
      description: gamePlan.description,
      thumbnail: gamePlan.thumbnail,
      flow_data: {
        nodes: gamePlan.flow_data?.nodes || [],
        edges: gamePlan.flow_data?.edges || [],
        thumbnail: gamePlan.thumbnail
      },
      is_public: (gamePlan.is_public || false) ? 1 : 0, // Convert to number for IndexedDB
      updated_at: new Date().toISOString(),
      _offline_updated: true,
      _sync_status: 'pending',
      _last_modified: new Date().toISOString()
    };

    await indexedDBService.gamePlans.update(updatedPlan.id, updatedPlan);

    // Queue for sync when online
    await offlineActionQueue.enqueueAction(
      'update_game_plan',
      updatedPlan as unknown as Record<string, unknown>,
      userId
    );

    return transformFromOfflineGamePlan(updatedPlan);
  }

  /**
   * Delete a game plan in offline mode
   */
  async deleteGamePlan(gamePlanId: string): Promise<void> {
    const { isAuthenticated, userId } = await this.checkPermissions();
    if (!isAuthenticated || !userId) {
      throw new Error('Authentication required to delete game plans');
    }

    await indexedDBService.initialize();
    
    // Get existing plan
    const existingPlan = await indexedDBService.gamePlans.get(gamePlanId);
    if (!existingPlan) {
      throw new Error('Game plan not found');
    }

    // Check ownership
    if (existingPlan.user_id !== userId) {
      throw new Error('You can only delete your own game plans');
    }

    await indexedDBService.gamePlans.delete(gamePlanId);

    // Queue for sync when online (only if it was synced before)
    if (existingPlan._sync_status === 'synced') {
      await offlineActionQueue.enqueueAction(
        'delete_game_plan',
        { id: gamePlanId },
        userId
      );
    }
  }
}

// Export singleton instance
export const offlineGamePlanService = OfflineGamePlanService.getInstance();
